/**
 * Canonical stack snapshot contract.
 *
 * Stack writes once via `buildStackSnapshot` → `snapshot_json` column.
 * Homie, `<MyStackCard>`, and `/lender-match` all read via `readStackSnapshot`
 * so a change in shape only needs to land in one place.
 */

export const STACK_SNAPSHOT_VERSION = 1 as const;

export type StackLayer = "federal" | "state" | "county" | "employer";

export interface StackProgram {
  name: string;
  provider?: string | null;
  layer: StackLayer;
  amount_min?: number | null;
  amount_max?: number | null;
  amount_text?: string | null;
  source_url?: string | null;
  // pass-through for anything the grant-stack function adds later
  [k: string]: unknown;
}

export interface StackSnapshot {
  version: typeof STACK_SNAPSHOT_VERSION;
  zip: string;
  state?: string | null;
  employer_name?: string | null;
  employer_status?: string | null;
  total_estimated: number;
  generated_at: string;
  programs: StackProgram[];
  by_layer: Record<StackLayer, StackProgram[]>;
}

type LayersInput = Partial<Record<StackLayer, any[]>> | null | undefined;

interface BuildInput {
  zip: string;
  state?: string | null;
  employer_name?: string | null;
  employer_status?: string | null;
  total_estimated?: number | null;
  generated_at?: string | null;
  layers: LayersInput;
}

const LAYERS: StackLayer[] = ["federal", "state", "county", "employer"];

const normalizeProgram = (raw: any, layer: StackLayer): StackProgram => ({
  ...(raw && typeof raw === "object" ? raw : {}),
  name: String(raw?.name ?? raw?.title ?? "Unnamed program"),
  provider: raw?.provider ?? null,
  layer,
  amount_min: raw?.amount_min ?? null,
  amount_max: raw?.amount_max ?? null,
  amount_text: raw?.amount_text ?? null,
  source_url: raw?.source_url ?? null,
});

export const buildStackSnapshot = (input: BuildInput): StackSnapshot => {
  const by_layer = LAYERS.reduce((acc, layer) => {
    const arr = Array.isArray(input.layers?.[layer]) ? (input.layers as any)[layer] : [];
    acc[layer] = arr.map((p: any) => normalizeProgram(p, layer));
    return acc;
  }, {} as Record<StackLayer, StackProgram[]>);

  const programs = LAYERS.flatMap((l) => by_layer[l]);

  return {
    version: STACK_SNAPSHOT_VERSION,
    zip: input.zip,
    state: input.state ?? null,
    employer_name: input.employer_name ?? null,
    employer_status: input.employer_status ?? null,
    total_estimated: input.total_estimated ?? 0,
    generated_at: input.generated_at ?? new Date().toISOString(),
    programs,
    by_layer,
  };
};


const LAST_STACK_SNAPSHOT_KEY = "ha_last_stack_snapshot";

export const saveLastStackSnapshot = (snapshot: StackSnapshot) => {
  try {
    localStorage.setItem(LAST_STACK_SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch (_) {
    /* storage unavailable */
  }
};

export const readLastStackSnapshot = (): StackSnapshot | null => {
  try {
    const raw = localStorage.getItem(LAST_STACK_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StackSnapshot>;
    if (parsed.version === STACK_SNAPSHOT_VERSION && Array.isArray(parsed.programs) && parsed.zip) {
      return parsed as StackSnapshot;
    }
  } catch (_) {
    /* ignore malformed or unavailable storage */
  }
  return null;
};

/**
 * Read a snapshot from a `grant_stack_results` row, with a graceful fallback
 * to the older `layers_json` shape so historical rows keep working even if
 * the backfill hasn't run yet.
 */
export const readStackSnapshot = (row: any): StackSnapshot | null => {
  if (!row) return null;
  if (row.snapshot_json && typeof row.snapshot_json === "object") {
    const s = row.snapshot_json as Partial<StackSnapshot>;
    if (s.version === STACK_SNAPSHOT_VERSION && Array.isArray(s.programs)) {
      return s as StackSnapshot;
    }
  }
  // Fallback: derive from legacy layers_json
  return buildStackSnapshot({
    zip: row.zip ?? "",
    employer_name: row.employer_name ?? null,
    total_estimated: row.total_estimated ?? 0,
    generated_at: row.created_at ?? null,
    layers: row.layers_json ?? null,
  });
};

/** Names suitable for passing to /lender-match `selected_grants`. */
export const snapshotGrantNames = (s: StackSnapshot | null): string[] => {
  if (!s) return [];
  return s.programs.map((p) => p.provider || p.name).filter(Boolean) as string[];
};
